// Shared lat/lon text-input validation for AddBuildingDialog (create) and
// BuildingManagerOverlay's location editor (patch) — one parser so the WGS84 bounds and the
// "empty means let the backend geocode" rule aren't re-typed in two places (#1363).

export type CoordinateFieldInput = { lat: string; lon: string };

export type ParsedCoordinateFields =
  | { ok: true; fields: { lat: number; lon: number } | null }
  | { ok: false; error: string };

/**
 * Parses a pair of lat/lon text inputs.
 *
 * - Both empty → `fields: null` — a valid result meaning "don't send lat/lon at all", i.e. let
 *   the backend geocode (or leave existing coordinates alone if nothing else changed). This is
 *   never the same as explicitly clearing coordinates, which the API has no way to express.
 * - Exactly one filled, or a value outside WGS84 bounds → an inline error, never silently
 *   dropped or clamped.
 */
export function parseCoordinateFields({
  lat,
  lon,
}: CoordinateFieldInput): ParsedCoordinateFields {
  const latRaw = lat.trim();
  const lonRaw = lon.trim();

  if (!latRaw && !lonRaw) return { ok: true, fields: null };
  if (!latRaw || !lonRaw) {
    return {
      ok: false,
      error: "Anna sekä leveys- että pituusaste, tai jätä molemmat tyhjäksi.",
    };
  }

  const latValue = Number(latRaw);
  if (!Number.isFinite(latValue) || latValue < -90 || latValue > 90) {
    return { ok: false, error: "Leveysasteen on oltava välillä -90…90." };
  }
  const lonValue = Number(lonRaw);
  if (!Number.isFinite(lonValue) || lonValue < -180 || lonValue > 180) {
    return { ok: false, error: "Pituusasteen on oltava välillä -180…180." };
  }
  return { ok: true, fields: { lat: latValue, lon: lonValue } };
}

/**
 * Builds the address/city portion of a location-edit PATCH body — included only when the
 * value actually changed from the building's current one.
 *
 * The backend treats any *present* `address`/`city` in a PATCH as "the address changed" and
 * re-geocodes, clearing `lat`/`lon` to `NULL` when the address doesn't resolve. Sending the
 * (unchanged) current values on every save — as if the operator had re-typed them — would
 * silently discard a coordinate correction on the very next unrelated edit. Omitting fields
 * that didn't change means an edit that only touches, say, the name never triggers a
 * re-geocode at all (#1358 verify fix).
 */
export function addressFieldsPatch(
  current: { address: string; city: string },
  draft: { address: string; city: string },
): { address?: string; city?: string } {
  const patch: { address?: string; city?: string } = {};
  if (draft.address !== current.address) patch.address = draft.address;
  if (draft.city !== current.city) patch.city = draft.city;
  return patch;
}
